<!--
  A controlled version of <details> that supports v-model.
  No styling* or DOM tree decisions are made here beyond how
  <details> works out of the box.

  * except for an experimental open/close animation for <details>
    (will gracefully degrade on browsers that don't support it)

  Usage as an exclusive accordion:
  ```
  <donut-details v-model="boundValue" :value="first">
    <summary>Actuator</summary>
    <div>Div with content</div>
  </donut-details>

  <donut-details v-model="boundValue" :value="second">
    <summary>Actuator</summary>
    <div>Div with content</div>
  </donut-details>
  ```

  Usage as an non-exclusive accordion:
  ```
  <donut-details v-model="boundValue1">
    <summary>Actuator</summary>
    <div>Div with content</div>
  </donut-details>

  <donut-details v-model="boundValue2">
    <summary>Actuator</summary>
    <div>Div with content</div>
  </donut-details>
  ```
-->
<template>
  <details ref="details" class="donut-details" :open="open">
    <slot />
  </details>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';

const emit = defineEmits(['update:modelValue']);
const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Object, Array],
    required: true,
  },
  /*
   * Open/closed state will be determined based on `value` matching `modelValue`
   * This allows you to render a list of <donut-details> and manage the currently open
   * instance based on a single state variable.
   */
  value: { type: [String, Number, Boolean, Object, Array], default: null },
});

const details = ref(null);
const open = computed(() => (
  // if props.value was set, determine open state based on props.modelValue matching props.value
  // otherwise consider props.modelValue as a boolean toggle
  props.value ? props.modelValue === props.value : props.modelValue
));

function onClick(event) {
  event.preventDefault();

  // Either update to `props.value` or toggle open state
  const newValue = props.value || !open.value;
  emit('update:modelValue', newValue);
}

onMounted(() => {
  const summary = details.value.querySelector('summary');

  if (summary) {
    summary.addEventListener('click', onClick);
  }
});

onUnmounted(() => {
  const summary = details.value?.querySelector('summary');

  if (summary) {
    summary.removeEventListener('click', onClick);
  }
});
</script>

<style scoped lang="scss">
.donut-details::details-content {
  height: 0;
  overflow: hidden;

  transition: block-size 0.25s, content-visibility 0.25s;

  /**
   * Experimental CSS!
   * Check support: https://developer.mozilla.org/en-US/docs/Web/CSS/interpolate-size#browser_compatibility
   */
  interpolate-size: allow-keywords;

  /**
   * Enable transition for content-visibility
   *  Check support: https://developer.mozilla.org/en-US/docs/Web/CSS/content-visibility#browser_compatibility
   */
  transition-behavior: allow-discrete;
}

/**
 * Not all browsers support the :open selector
 * Using [open] attribute selector instead
 */
.donut-details[open]::details-content {
   height: auto;
}
</style>
